document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const revealFlavourCards = (cards) => {
  cards.forEach((card, index) => {
    card.style.setProperty('--card-delay', reducedMotion ? '0ms' : `${Math.min(index, 7) * 55}ms`);
  });
  window.requestAnimationFrame(() => cards.forEach((card) => card.classList.add('is-card-visible')));
};

const initializeFlavourSections = (scope = document) => {
  scope.querySelectorAll('[data-flavour-section]').forEach((section) => {
    revealFlavourCards([...section.querySelectorAll('[data-flavour-card]:not([hidden])')]);
    const button = section.querySelector('[data-flavour-toggle]');
    if (!button || button.dataset.bound === 'true') return;

    button.dataset.bound = 'true';
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      section.querySelectorAll('.tawfi-flavour-card--extra').forEach((card) => {
        card.toggleAttribute('hidden', !open);
      });
      if (open) revealFlavourCards([...section.querySelectorAll('.tawfi-flavour-card--extra')]);
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.closeLabel : button.dataset.openLabel;
    });
  });
};

const initializeProductForms = (scope = document) => {
  scope.querySelectorAll('[data-variant-select]').forEach((select) => {
    if (select.dataset.bound === 'true') return;
    select.dataset.bound = 'true';

    const updateVariantState = () => {
      const option = select.options[select.selectedIndex];
      const product = select.closest('.tawfi-product-info');
      const price = product?.querySelector('[data-product-price]');
      const button = product?.querySelector('[data-add-to-cart]');
      const enquiry = product?.querySelector('[data-stock-enquiry]');
      const available = option?.dataset.available === 'true';
      const builderControlsButton = Boolean(product?.querySelector('[data-box-builder], [data-chocolate-builder]'));

      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      if (button && !builderControlsButton) {
        button.disabled = !available;
        button.textContent = available ? 'Add to bag' : 'Sold out';
      }
      if (enquiry) enquiry.toggleAttribute('hidden', available);
    };

    select.addEventListener('change', updateVariantState);
    updateVariantState();
  });
};

const BOX_RULES = {
  mejdoul: {
    8: [[8, 1], [2, 4], [4, 2]],
    18: [[6, 3], [2, 9], [1, 18]],
    36: [[9, 4], [4, 9], [12, 3]]
  },
  khalas: {
    9: [[9, 1], [3, 3], [1, 9]],
    16: [[2, 8], [4, 4]],
    36: [[6, 6], [2, 18], [1, 36]],
    72: [[9, 8], [4, 18], [12, 6]]
  }
};

const initializeBoxBuilders = (scope = document) => {
  scope.querySelectorAll('[data-box-builder]').forEach((builder) => {
    if (builder.dataset.bound === 'true') return;
    builder.dataset.bound = 'true';

    const product = builder.closest('.tawfi-product-info');
    const form = builder.closest('form');
    const button = form?.querySelector('[data-add-to-cart]');
    const pieceCount = builder.querySelector('[data-box-piece-count]');
    const optionsRoot = builder.querySelector('[data-composition-options]');
    const slotsRoot = builder.querySelector('[data-flavour-slots]');
    const status = builder.querySelector('[data-box-status]');
    const flavourProgress = builder.querySelector('[data-flavour-progress]');
    const loading = builder.querySelector('[data-builder-loading]');
    const progressItems = [...builder.querySelectorAll('[data-builder-progress]')];
    const compositionProperty = builder.querySelector('[data-composition-property]');
    const flavoursProperty = builder.querySelector('[data-flavours-property]');
    const flavourData = builder.querySelector('[data-box-flavours]');
    const variantSelect = product?.querySelector('[data-variant-select]');
    let flavours = [];
    let activeRule = null;
    let variantAvailable = true;

    try {
      flavours = JSON.parse(flavourData?.textContent || '[]').map((item) => item.name).filter(Boolean);
    } catch (error) {
      flavours = [];
    }

    const validate = () => {
      const selects = [...slotsRoot.querySelectorAll('select')];
      const chosen = selects.map((select) => select.value).filter(Boolean);
      const uniqueChosen = new Set(chosen);
      const hasDuplicates = uniqueChosen.size !== chosen.length;
      const complete = activeRule && chosen.length === activeRule[0] && !hasDuplicates;

      selects.forEach((select) => {
        select.setCustomValidity(select.value && chosen.filter((value) => value === select.value).length > 1 ? 'Choose a different flavour for each slot.' : '');
        const otherValues = new Set(selects.filter((item) => item !== select).map((item) => item.value).filter(Boolean));
        [...select.options].forEach((option) => {
          if (option.value) option.disabled = otherValues.has(option.value) && option.value !== select.value;
        });
      });

      if (!variantAvailable) {
        status.textContent = 'This box size is currently unavailable. Disable inventory tracking or enable continue selling in Shopify.';
      } else if (!flavours.length) {
        status.textContent = 'Enable flavours in the Main product section of the theme editor.';
      } else if (!activeRule) {
        status.textContent = 'Choose an arrangement to continue.';
      } else if (hasDuplicates) {
        status.textContent = 'Each slot needs a different flavour. Replace the repeated selection to continue.';
      } else if (!complete) {
        const remaining = activeRule[0] - uniqueChosen.size;
        status.textContent = `Choose ${remaining} more different flavour${remaining === 1 ? '' : 's'} to complete this box.`;
      } else {
        status.textContent = 'Your box is complete and ready to add.';
      }

      if (flavourProgress) flavourProgress.textContent = activeRule ? `${uniqueChosen.size} of ${activeRule[0]} different flavours selected` : '';
      progressItems.forEach((item, index) => {
        item.classList.toggle('is-complete', index === 0 || (index === 1 && Boolean(activeRule)) || (index === 2 && complete));
        item.classList.toggle('is-active', (!activeRule && index === 1) || (activeRule && !complete && index === 2));
      });

      if (compositionProperty) compositionProperty.value = activeRule ? `${activeRule[0]} flavour${activeRule[0] === 1 ? '' : 's'} × ${activeRule[1]} piece${activeRule[1] === 1 ? '' : 's'}` : '';
      if (flavoursProperty) flavoursProperty.value = complete ? chosen.map((name) => `${name} × ${activeRule[1]}`).join(', ') : '';
      if (button) {
        button.disabled = !variantAvailable || !complete;
        button.textContent = !variantAvailable ? 'Sold out' : complete ? 'Add to bag' : 'Complete your box';
      }
      return complete;
    };

    const renderSlots = (rule) => {
      activeRule = rule;
      slotsRoot.replaceChildren();
      for (let index = 0; index < rule[0]; index += 1) {
        const field = document.createElement('label');
        field.className = 'tawfi-flavour-slot';
        field.toggleAttribute('hidden', index !== 0);
        const label = document.createElement('span');
        label.textContent = `Flavour ${index + 1} · ${rule[1]} piece${rule[1] === 1 ? '' : 's'}`;
        const select = document.createElement('select');
        select.required = index === 0;
        select.setAttribute('aria-label', `Flavour ${index + 1}`);
        const emptyOption = document.createElement('option');
        emptyOption.value = '';
        emptyOption.textContent = 'Select flavour';
        select.append(emptyOption);
        flavours.forEach((name) => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          select.append(option);
        });
        select.addEventListener('change', () => {
          validate();
          const duplicate = [...slotsRoot.querySelectorAll('select')].filter((item) => item.value === select.value && select.value).length > 1;
          const nextField = field.nextElementSibling;
          if (!select.value || duplicate || !nextField || !nextField.hidden) return;
          if (loading) loading.hidden = false;
          window.setTimeout(() => {
            nextField.hidden = false;
            const nextSelect = nextField.querySelector('select');
            if (nextSelect) nextSelect.required = true;
            if (loading) loading.hidden = true;
            nextField.classList.add('is-arriving');
            nextSelect?.focus({ preventScroll: true });
            validate();
          }, reducedMotion ? 0 : 260);
        });
        field.append(label, select);
        slotsRoot.append(field);
      }
      validate();
    };

    const renderVariant = (option, available) => {
      variantAvailable = available;
      const sizeText = option?.dataset.boxSize || option?.textContent || '';
      const size = Number(sizeText.match(/\d+/)?.[0]);
      const rules = BOX_RULES[builder.dataset.boxType]?.[size] || [];
      if (pieceCount) pieceCount.textContent = size ? `${size} pieces` : '';
      optionsRoot.replaceChildren();
      activeRule = null;
      slotsRoot.replaceChildren();

      rules.forEach((rule, index) => {
        const label = document.createElement('label');
        label.className = 'tawfi-composition-option';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = `box-composition-${builder.dataset.boxType}`;
        radio.addEventListener('change', () => {
          if (loading) loading.hidden = false;
          window.setTimeout(() => {
            renderSlots(rule);
            if (loading) loading.hidden = true;
            slotsRoot.querySelector('select')?.focus({ preventScroll: true });
          }, reducedMotion ? 0 : 260);
        });
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = `${rule[0]} flavour${rule[0] === 1 ? '' : 's'}`;
        const detail = document.createElement('small');
        detail.textContent = `${rule[1]} piece${rule[1] === 1 ? '' : 's'} each`;
        copy.append(title, detail);
        label.append(radio, copy);
        optionsRoot.append(label);
      });

      validate();
    };

    variantSelect?.addEventListener('change', () => {
      const option = variantSelect.options[variantSelect.selectedIndex];
      if (loading) loading.hidden = false;
      window.setTimeout(() => {
        renderVariant(option, option?.dataset.available === 'true');
        if (loading) loading.hidden = true;
      }, reducedMotion ? 0 : 260);
    });
    form?.addEventListener('submit', (event) => {
      if (!validate()) {
        event.preventDefault();
        slotsRoot.querySelector('select:invalid')?.focus();
      }
    });

    const selectedOption = variantSelect?.options[variantSelect.selectedIndex];
    renderVariant(selectedOption, selectedOption?.dataset.available === 'true');
  });
};

const initializeChocolateJourneys = (scope = document) => {
  scope.querySelectorAll('[data-chocolate-journey]').forEach((journey) => {
    if (journey.dataset.bound === 'true') return;
    journey.dataset.bound = 'true';
    const steps = [...journey.querySelectorAll('[data-journey-step]')];
    const progress = [...journey.querySelectorAll('.tawfi-journey-progress span')];
    const loading = journey.querySelector('[data-journey-loading]');

    const revealStep = (stepNumber) => {
      const step = steps.find((item) => Number(item.dataset.journeyStep) === stepNumber);
      if (!step || !step.hidden) return;
      if (loading) loading.hidden = false;
      window.setTimeout(() => {
        step.hidden = false;
        step.classList.add('is-active');
        progress.forEach((item, index) => {
          item.classList.toggle('is-complete', index + 1 < stepNumber);
          item.classList.toggle('is-active', index + 1 === stepNumber);
        });
        if (loading) loading.hidden = true;
        step.querySelector('input,select')?.focus({ preventScroll: true });
      }, reducedMotion ? 0 : 280);
    };

    steps.forEach((step) => {
      const number = Number(step.dataset.journeyStep);
      step.querySelectorAll('input[type="radio"]').forEach((input) => input.addEventListener('change', () => revealStep(number + 1)));
    });
  });
};

const initializeChocolateBuilders = (scope = document) => {
  scope.querySelectorAll('[data-chocolate-builder]').forEach((builder) => {
    if (builder.dataset.bound === 'true') return;
    builder.dataset.bound = 'true';
    const property = builder.querySelector('[data-chocolate-flavours-property]');
    const checkboxes = [...builder.querySelectorAll('[data-chocolate-flavour]')];
    const allFields = [...builder.querySelectorAll('.tawfi-chocolate-fields > label')];
    const fields = allFields.filter((field) => field.querySelector('select'));
    const optionalTheme = allFields.find((field) => !field.querySelector('select'));
    const flavourFieldset = builder.querySelector('.tawfi-chocolate-flavours');
    const notes = builder.querySelector('[data-chocolate-notes]');
    const loading = builder.querySelector('[data-chocolate-product-loading]');
    const form = builder.closest('form');
    const button = form?.querySelector('[data-add-to-cart]');
    const product = builder.closest('.tawfi-product-info');
    const variantSelect = product?.querySelector('[data-variant-select]');
    const progressItems = [...builder.querySelectorAll('.tawfi-builder-progress span')];
    const requiredControls = fields.map((field) => field.querySelector('select')).filter(Boolean);

    allFields.forEach((field, index) => field.toggleAttribute('hidden', index !== 0));
    if (flavourFieldset) flavourFieldset.hidden = true;
    if (notes) notes.hidden = true;
    const validate = () => {
      const variantOption = variantSelect?.options[variantSelect.selectedIndex];
      const variantAvailable = !variantOption || variantOption.dataset.available === 'true';
      const completedFields = requiredControls.filter((control) => Boolean(control.value)).length;
      const complete = variantAvailable && completedFields === requiredControls.length;

      progressItems.forEach((item, index) => {
        const stepComplete = index === 0
          ? variantAvailable
          : index <= requiredControls.length
            ? Boolean(requiredControls[index - 1]?.value)
            : complete;
        const activeIndex = !variantAvailable ? 0 : Math.min(completedFields + 1, progressItems.length - 1);
        item.classList.toggle('is-complete', stepComplete);
        item.classList.toggle('is-active', !complete && index === activeIndex);
      });

      if (button) {
        button.disabled = !complete;
        button.textContent = !variantAvailable ? 'Sold out' : complete ? 'Add to bag' : 'Complete your chocolate box';
      }
      return complete;
    };

    const reveal = (element) => {
      if (!element || !element.hidden) return;
      if (loading) loading.hidden = false;
      window.setTimeout(() => {
        element.hidden = false;
        element.classList.add('is-arriving');
        if (loading) loading.hidden = true;
        element.querySelector('select,input,textarea')?.focus({ preventScroll: true });
      }, reducedMotion ? 0 : 260);
    };

    fields.forEach((field, index) => {
      const control = field.querySelector('select,input');
      control?.addEventListener('change', () => {
        if (control.value) {
          const next = fields[index + 1] || flavourFieldset;
          reveal(next);
          if (index === fields.length - 1) reveal(optionalTheme);
        }
        validate();
      });
    });
    const sync = () => {
      const selected = checkboxes.filter((box) => box.checked).map((box) => box.value);
      if (property) property.value = selected.join(', ');
    };
    checkboxes.forEach((box) => box.addEventListener('change', sync));
    checkboxes.forEach((box) => box.addEventListener('change', () => reveal(notes)));
    variantSelect?.addEventListener('change', validate);
    form?.addEventListener('submit', (event) => {
      if (validate()) return;
      event.preventDefault();
      requiredControls.find((control) => !control.value)?.focus();
    });
    sync();
    validate();
  });
};

window.addEventListener('DOMContentLoaded', () => {
  document.body.classList.add('is-loaded');
  initializeFlavourSections();
  initializeProductForms();
  initializeBoxBuilders();
  initializeChocolateBuilders();
  initializeChocolateJourneys();

  const items = document.querySelectorAll('[data-reveal]');
  if (reducedMotion || !('IntersectionObserver' in window)) {
    items.forEach((item) => item.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    items.forEach((item) => observer.observe(item));
  }

  const header = document.querySelector('[data-site-header]');
  const progress = document.querySelector('[data-scroll-progress]');
  let headerTicking = false;
  let headerScrolled = null;
  const updateHeader = () => {
    headerTicking = false;
    if (progress) {
      const available = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.setProperty('--tawfi-scroll', String(available > 0 ? Math.min(window.scrollY / available, 1) : 0));
    }
    const nextScrolled = window.scrollY > 24;
    if (nextScrolled === headerScrolled) return;
    headerScrolled = nextScrolled;
    header?.classList.toggle('is-scrolled', nextScrolled);
  };
  updateHeader();
  window.addEventListener('scroll', () => {
    if (headerTicking) return;
    headerTicking = true;
    window.requestAnimationFrame(updateHeader);
  }, { passive: true });

  document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
    const menu = document.querySelector('[data-mobile-menu]');
    const closeMenu = () => {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open menu');
      menu?.setAttribute('hidden', '');
    };
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu?.toggleAttribute('hidden', !open);
    });
    menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
        closeMenu();
        button.focus();
      }
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const stage = gallery.querySelector('[data-gallery-stage]');
    const toggleZoom = () => {
      if (!stage?.querySelector('img')) return;
      const zoomed = stage.classList.toggle('is-zoomed');
      stage.setAttribute('aria-pressed', String(zoomed));
    };
    stage?.addEventListener('click', toggleZoom);
    gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const image = thumb.querySelector('img');
        const stageImage = stage?.querySelector('img');
        if (!image || !stageImage) return;
        stageImage.src = image.dataset.full || image.src;
        stageImage.alt = image.alt;
        stage.classList.remove('is-zoomed');
        stage.setAttribute('aria-pressed', 'false');
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  });
});

document.addEventListener('shopify:section:load', (event) => {
  initializeFlavourSections(event.target);
  initializeProductForms(event.target);
  initializeBoxBuilders(event.target);
  initializeChocolateBuilders(event.target);
  initializeChocolateJourneys(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const card = event.target.closest('[data-flavour-card]');
  if (!card || !card.hidden) return;
  const section = card.closest('[data-flavour-section]');
  const button = section?.querySelector('[data-flavour-toggle]');
  section?.querySelectorAll('.tawfi-flavour-card--extra').forEach((item) => item.removeAttribute('hidden'));
  if (section) revealFlavourCards([...section.querySelectorAll('.tawfi-flavour-card--extra')]);
  if (button) {
    button.setAttribute('aria-expanded', 'true');
    button.textContent = button.dataset.closeLabel;
  }
});
